import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { getMarketplaceAttributionParams } from "@/utils/functions/marketplaceAttribution";
import { resolveClientMarketplaceAttribution } from "@/utils/functions/marketplaceAttributionSession";

/**
 * Pure resolution used by the hook. Exported so SSR vs post-mount behavior can
 * be unit-tested without fighting React Testing Library's automatic effect flush.
 */
export function attributionSourceForRender(
  search: string,
  canUseStoredAttribution: boolean,
): URLSearchParams {
  if (!canUseStoredAttribution) {
    return getMarketplaceAttributionParams(search);
  }

  return resolveClientMarketplaceAttribution(search);
}

/**
 * Approved marketplace UTMs for the current page.
 *
 * Cookie/session fallback is deferred until after mount so the first client
 * render matches SSR. Without that gate, full loads with a cookie but no URL
 * UTMs would hydrate-mismatch every attributed href.
 */
export default function useResolvedMarketplaceAttribution(): URLSearchParams {
  const searchParams = useSearchParams();
  const search = searchParams?.toString() ?? "";
  const [canUseStoredAttribution, setCanUseStoredAttribution] = useState(false);

  useEffect(() => {
    setCanUseStoredAttribution(true);
  }, []);

  return useMemo(
    () => attributionSourceForRender(search, canUseStoredAttribution),
    [canUseStoredAttribution, search],
  );
}
